import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IoIosArrowBack } from "react-icons/io";
import { DatePicker } from "@mui/x-date-pickers";
import { Autocomplete, FormControl, Select, MenuItem, TextField, Checkbox, RadioGroup, Radio, FormControlLabel,} from "@mui/material";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { createEntity } from "../../../apis/apiService";
import { BtnLoader } from "../../common/Loader";


export const MilestonLogsExportComponent = ({
  milestoneName,
  projectId,
  onCancel,
  sourceData, // The raw/filtered source data from TimesheetTable
  viewType,
  projectSpan,
  fromDate,
  toDate,
  milestoneId,
}) => {
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const { role: login_user_role, id: login_user_id, name: login_user_name } = login_user;
  const [loading, setLoading] = useState(false);

  const timesheetViewOptions = useSelector(
    (state) => state.selectOptions?.timesheetViewOptions || {}
  );

  const timesheetViewOptionsArray = Object.entries(timesheetViewOptions).map(
    ([key, value]) => ({
      title: value,
      value: key,
    })
  );

  // Extract unique users from the milestone sourceData
  const milestoneUsers = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const usersMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.owner_id) {
            usersMap.set(log.owner_id, log.ownerName || group.userName);
          }
        });
      }
    });
    return Array.from(usersMap.entries()).map(([id, name]) => ({
      id: Number(id),
      title: name,
    }));
  }, [sourceData]);

  // Extract unique tasks from the milestone sourceData
  const milestoneTasks = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const tasksMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.task_id || log.log_type === "TASK") {
            const taskName = log.task_general_issue || log.user;
            if (taskName) {
              const id = log.task_id || taskName;
              tasksMap.set(id, {
                id: id,
                title: taskName,
              });
            }
          }
        });
      }
    });
    return Array.from(tasksMap.values());
  }, [sourceData]);

  // Extract unique issues from the milestone sourceData
  const milestoneIssues = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const issuesMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.issue_id || log.log_type === "ISSUE") {
            const issueName = log.task_general_issue || log.user;
            if (issueName) {
              const id = log.issue_id || issueName;
              issuesMap.set(id, {
                id: id,
                title: issueName,
              });
            }
          }
        });
      }
    });
    return Array.from(issuesMap.values());
  }, [sourceData]);

  const {
    handleSubmit,
    control,
    reset,
  } = useForm({
    defaultValues: {
      Users: login_user_role === "EMPLOYEE" ? [login_user_id] : [],
      Tasks: [],
      Issues: [],
      StartDate: null,
      EndDate: null,
      billingType: "",
      createdBy: "",
      timesheetView: [],
      format: "excel",
      timesheet_type: "",
      type: "totalHours",
    },
  });

  const onSubmit = async (formData) => {
    setLoading(true);

    const selectedTaskIds = [];
    const selectedIssueIds = [];

    // Parse tasks from formData.Tasks
    if (Array.isArray(formData.Tasks) && formData.Tasks.length > 0) {
      formData.Tasks.forEach((taskId) => {
        if (!selectedTaskIds.includes(taskId)) {
          selectedTaskIds.push(taskId);
        }
      });
    }

    // Parse issues from formData.Issues
    if (Array.isArray(formData.Issues) && formData.Issues.length > 0) {
      formData.Issues.forEach((issueId) => {
        if (!selectedIssueIds.includes(issueId)) {
          selectedIssueIds.push(issueId);
        }
      });
    }

    // Check if at least one value/filter is selected
    const hasUsers = Array.isArray(formData.Users) && formData.Users.length > 0;
    const hasTasks = selectedTaskIds.length > 0;
    const hasIssues = selectedIssueIds.length > 0;
    const hasStartDate = !!formData.StartDate;
    const hasEndDate = !!formData.EndDate;
    const hasBillingType = !!formData.billingType;
    const hasTimesheetType = !!formData.timesheet_type;

    if (!hasUsers && !hasTasks && !hasIssues && !hasStartDate && !hasEndDate && !hasBillingType && !hasTimesheetType) {
      toast.warning("No values selected for export, please select values.");
      setLoading(false);
      return;
    }

    const requestedData = {
      users: formData.Users,
      tasks: selectedTaskIds,
      projectId: Number(projectId),
      issues: selectedIssueIds,
      fromDate: formData.StartDate ? format(new Date(formData.StartDate), "yyyy-MM-dd") : null,
      toDate: formData.EndDate ? format(new Date(formData.EndDate), "yyyy-MM-dd") : null,
      type: formData.type || "totalHours",
      milestone_id: milestoneId ? Number(milestoneId) : null,
      format: "excel",
    };


    try {
      const response = await createEntity("milestones/exportTimesheet", requestedData);
      if (response && response.url) {
        const doc_url = response.url.replace(/\\/g, "/");
        const link = document.createElement("a");
        link.href = doc_url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onCancel();
        toast.success(response.message || "Export successful.");
      } 
      else {
        throw new Error("Invalid document URL returned");
      }
    } 
    catch (error) {
      toast.error(error.message || "Error exporting timesheet");
    } 
    finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    reset();
  };

  return (
    <div>
      {/* Header Section */}
      <div className="d-flex align-items-center bg-white justify-content-between px-4 py-3">
        <button onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack className=" text-rgba-gray" />
            Export Milestone Timelog
          </div>
        </button>
        <div>
          <button
            type="button"
            onClick={handleClear}
            className="R_btn tex-tgba-red clear-filter-btn"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {/* Filter Form */}
      <div className="bg-white px-4 py-3">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="g-3 mb-2 row">
              {/* Users field */}
              <div className="col-md-12">
                <label htmlFor="Users" className="form-label">
                  Users
                </label>
                {login_user_role === "EMPLOYEE" ? (
                  <FormControl fullWidth>
                    <Select value={login_user_id}>
                      <MenuItem value={login_user_id}>
                        {login_user_name}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Controller
                    name="Users"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      const ALL_OPTION = { id: "all", title: "Select All" };
                      const allSelected =
                        Array.isArray(value) &&
                        milestoneUsers.length > 0 &&
                        value.length === milestoneUsers.length &&
                        milestoneUsers.every((user) => value.includes(user.id));

                      const optionsWithAll = milestoneUsers.length > 0 ? [ALL_OPTION, ...milestoneUsers] : [];
                      const selectedValues =
                        Array.isArray(value) && value.length === 0
                          ? []
                          : milestoneUsers.filter((user) => value?.includes(user.id));

                      const handleUserChange = (event, newValue) => {
                        const isAllSelected = newValue.some((val) => val.id === "all");

                        if (isAllSelected) {
                          const isCurrentlyAllSelected =
                            value.length === milestoneUsers.length &&
                            milestoneUsers.every((user) => value.includes(user.id));

                          if (isCurrentlyAllSelected) {
                            onChange([]);
                          } else {
                            onChange(milestoneUsers.map((user) => user.id));
                          }
                        } else {
                          onChange(newValue.map((val) => val.id));
                        }
                      };

                      return (
                        <Autocomplete
                          multiple
                          id="Users"
                          options={optionsWithAll}
                          disableCloseOnSelect
                          getOptionLabel={(option) =>
                            option.id === "all" ? "Select All" : option.title
                          }
                          value={selectedValues}
                          onChange={handleUserChange}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} className="d-flex align-items-center mb-2">
                              <Checkbox
                                style={{ marginRight: 8 }}
                                checked={option.id === "all" ? allSelected : selected}
                              />
                              <strong>{option.title}</strong>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Users" />
                          )}
                        />
                      );
                    }}
                  />
                )}
              </div>

              {/* Tasks field */}
              <div className="col-md-12 mt-3">
                <label htmlFor="Tasks" className="form-label">
                  Tasks
                </label>
                <Controller
                  name="Tasks"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const ALL_OPTION = { id: "all", title: "Select All" };
                    const allSelected =
                      Array.isArray(value) &&
                      milestoneTasks.length > 0 &&
                      value.length === milestoneTasks.length &&
                      milestoneTasks.every((task) => value.includes(task.id));

                    const optionsWithAll = milestoneTasks.length > 0 ? [ALL_OPTION, ...milestoneTasks] : [];
                    const selectedValues =
                      Array.isArray(value) && value.length === 0
                        ? []
                        : milestoneTasks.filter((task) => value?.includes(task.id));

                    const handleTaskChange = (event, newValue) => {
                      const isAllSelected = newValue.some((val) => val.id === "all");

                      if (isAllSelected) {
                        const isCurrentlyAllSelected =
                          value.length === milestoneTasks.length &&
                          milestoneTasks.every((task) => value.includes(task.id));

                        if (isCurrentlyAllSelected) {
                          onChange([]);
                        } else {
                          onChange(milestoneTasks.map((task) => task.id));
                        }
                      } else {
                        onChange(newValue.map((val) => val.id));
                      }
                    };

                    return (
                      <Autocomplete
                        multiple
                        id="Tasks"
                        options={optionsWithAll}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          option.id === "all" ? "Select All" : option.title
                        }
                        value={selectedValues}
                        onChange={handleTaskChange}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={option.id === "all" ? allSelected : selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Tasks" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              {/* Issues field */}
              <div className="col-md-12 mt-3">
                <label htmlFor="Issues" className="form-label">
                  Issues
                </label>
                <Controller
                  name="Issues"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const ALL_OPTION = { id: "all", title: "Select All" };
                    const allSelected =
                      Array.isArray(value) &&
                      milestoneIssues.length > 0 &&
                      value.length === milestoneIssues.length &&
                      milestoneIssues.every((issue) => value.includes(issue.id));

                    const optionsWithAll = milestoneIssues.length > 0 ? [ALL_OPTION, ...milestoneIssues] : [];
                    const selectedValues =
                      Array.isArray(value) && value.length === 0
                        ? []
                        : milestoneIssues.filter((issue) => value?.includes(issue.id));

                    const handleIssueChange = (event, newValue) => {
                      const isAllSelected = newValue.some((val) => val.id === "all");

                      if (isAllSelected) {
                        const isCurrentlyAllSelected =
                          value.length === milestoneIssues.length &&
                          milestoneIssues.every((issue) => value.includes(issue.id));

                        if (isCurrentlyAllSelected) {
                          onChange([]);
                        } else {
                          onChange(milestoneIssues.map((issue) => issue.id));
                        }
                      } else {
                        onChange(newValue.map((val) => val.id));
                      }
                    };

                    return (
                      <Autocomplete
                        multiple
                        id="Issues"
                        options={optionsWithAll}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          option.id === "all" ? "Select All" : option.title
                        }
                        value={selectedValues}
                        onChange={handleIssueChange}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={option.id === "all" ? allSelected : selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Issues" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              {/* Start Date & End Date */}
              <div className="col-md-6 mt-3">
                <label htmlFor="StartDate">Start date</label>
                <div className="d-flex flex-column w-100">
                  <Controller
                    name="StartDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        format="DD/MM/YYYY"
                        onChange={(date) => field.onChange(date)}
                        renderInput={(params) => (
                          <TextField {...params} className="form-control" />
                        )}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="col-md-6 mt-3">
                <label htmlFor="EndDate">End date</label>
                <div className="d-flex flex-column w-100">
                  <Controller
                    control={control}
                    name="EndDate"
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        onChange={(date) => field.onChange(date)}
                        format="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField {...params} className="form-control" />
                        )}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Billing Type */}
              <div className="col-md-6 mt-3">
                <label htmlFor="billingType" className="form-label">
                  Select Billing Type
                </label>
                <Controller
                  name="billingType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <span className="lightText">Select Billing Type</span>
                          )
                        }
                      >
                        <MenuItem value="BILLABLE">BILLABLE</MenuItem>
                        <MenuItem value="NON-BILLABLE">NON-BILLABLE</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
              
              {/* Timesheet Type */}
              <div className="col-md-6 mt-3">
                <label htmlFor="timesheet_type" className="form-label">
                  Timesheet Type
                </label>
                <Controller
                  name="timesheet_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? (
                            selected === "REGULAR" ? "Regular" : "OT"
                          ) : (
                            <span className="lightText">Select Timesheet Type</span>
                          )
                        }
                      >
                        <MenuItem value="REGULAR">Regular</MenuItem>
                        <MenuItem value="OT">OT</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </div>

              {/* Type Field (Total Hours vs Individual Hours) */}
              <div className="col-md-12 mt-3">
                <label htmlFor="type" className="form-label">
                  Type <span className="errorMsg">*</span>
                </label>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: "Type field is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="totalHours"
                          control={<Radio />}
                          label="Total hours"
                        />
                        <FormControlLabel
                          value="individualHours"
                          control={<Radio />}
                          label="Individual hours"
                        />
                      </RadioGroup>
                      {error && (
                        <p className="errorMsg">{error.message}</p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="d-flex bg-white position-absolute w-100 bottom-0 mt-5 py-3">
              <button type="submit" className="add_btn me-3" disabled={loading}>
                {loading ? <BtnLoader /> : "Apply"}
              </button>
              <button type="button" className="cancel_btn" disabled={loading} onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default MilestonLogsExportComponent;
